import { useIncidentDetail } from 'core/components/JianDetail/hooks/useIncidentDetail';
import {
  IncidentTypeEnum,
  EmsTeamMemberPositionEnum,
  BaseInfoSexEnum,
  BaseInfoOccupationEnum,
  BaseInfoRelatedPersonRelationshipEnum,
  BaseInfoRelatedPersonAttendantArrivalTypeEnum,
  BaseInfoCustodyItemEnum,
  BaseInfoCollaborationTimingEnum,
  BaseInfoCollaborationEnum,
  RequestSummaryMedicalHistoryEnum,
  RequestSummaryADLEnum,
  RequestSummaryADLLevelEnum,
  RequestSummaryOnsetTypeEnum,
  MeasureTimingEnum,
  TrafficAccidentSituationEnum,
  TrafficAccidentAccidentTypeEnum,
  DrCarDrHeliDispatchEnum,
  OperationCriteriaDetailEnum,
  VitalO2MeanEnum,
  VitalBtPartEnum,
  VitalBpPartEnum,
  VitalO2FlowConditionEnum,
  ECGWaveformEnum,
  ECGWaveRemarksEnum,
  PupilReactionEnum,
  IntubationDevice,
  TreatmentIvAccessTimingEnum,
  AdministeredMedication,
  FindEmsContactSafetyEquipmentsEnum,
  JUST7Enum,
  FindEmsContactParalysisPartsEnum,
  FindEmsContactParalysisSymptomsEnum,
  FindEmsContactSpasmPartsEnum,
  FindEmsContactBleedingTypeEnum,
  TreatmentGeneralTreatmentsEnum,
  TreatmentROSCTreatmentsEnum,
  TreatmentROSCData,
  TreatmentCPADataTimingEnum,
  TreatmentCPADataConfidenceTypeEnum,
  TreatmentBsCPRDataConfidenceTypeEnum,
  TreatmentBsCPRDataTimingEnum,
  TreatmentAgreedByEnum,
  TreatmentExecuteTreatmentEnum,
  TreatmentInitialEcgEnum,
  TreatmentIVAccessExecuteEnum,
  TreatmentIVAccessSideEnum,
  TreatmentIVAccessPartEnum,
  TreatmentInfusionRateEnum,
  TreatmentAirwayManagementDeviceTypeEnum,
  TreatmentAirwayManagementLaryngoscopyEnum,
  TreatmentAirwayManagementCuffValueTypeEnum,
  TreatmentAirwayManagementVentilationEnum,
  TreatmentAirwayManagementExecuteEnum,
  TreatmentROSCECGWaveFormEnum,
} from 'core/gen/schemas';

/**
 * 事案種別の日本語ラベルマッピング
 */
const INCIDENT_TYPE_LABELS = {
  [IncidentTypeEnum.FIRE]: '火災',
  [IncidentTypeEnum.NATURAL_DISASTER]: '自然災害',
  [IncidentTypeEnum.DROWNING]: '水難',
  [IncidentTypeEnum.TRAFFIC]: '交通事故',
  [IncidentTypeEnum.WORK_ACCIDENT]: '労働災害',
  [IncidentTypeEnum.SPORTS]: 'スポーツ外傷',
  [IncidentTypeEnum.GENERAL_INJURY]: '一般外傷',
  [IncidentTypeEnum.ASSAULT]: '暴行',
  [IncidentTypeEnum.SELF_INJURY]: '自損行為',
  [IncidentTypeEnum.SUDDEN_ILLNESS]: '急病',
  [IncidentTypeEnum.HOSPITAL_TRANSFER]: '転院搬送',
  [IncidentTypeEnum.HOSPITAL_TRANSFER_SUPPORT]: '転院搬送支援',
  [IncidentTypeEnum.DOCTOR_TRANSPORT]: '医師搬送',
  [IncidentTypeEnum.EQUIPMENT_TRANSPORT]: '資器材搬送',
  [IncidentTypeEnum.OTHER]: 'その他',
} as const satisfies Record<keyof typeof IncidentTypeEnum, string>;

/**
 * 事案種別の日本語ラベルを取得する関数
 *
 * @param incidentType - 事案種別
 * @returns 日本語ラベル
 */
export const getIncidentTypeLabel = (
  incidentType: keyof typeof IncidentTypeEnum | null | undefined
): string => {
  if (!incidentType || !(incidentType in INCIDENT_TYPE_LABELS)) {
    return '不明';
  }

  return INCIDENT_TYPE_LABELS[incidentType];
};

/**
 * 救急隊員の役職の日本語ラベルマッピング
 */
const EMS_TEAM_MEMBER_POSITION_LABELS = {
  [EmsTeamMemberPositionEnum.LEADER]: '隊長',
  [EmsTeamMemberPositionEnum.SUB_LEADER]: '副隊長',
  [EmsTeamMemberPositionEnum.MEMBER]: '隊員',
} as const satisfies Record<keyof typeof EmsTeamMemberPositionEnum, string>;

/**
 * 救急隊員の役職の日本語ラベルを取得する関数
 *
 * @param emsTeamMemberPosition - 救急隊員の役職
 * @returns 日本語ラベル
 */
export const getEmsTeamMemberPositionLabel = (
  emsTeamMemberPosition:
    | keyof typeof EmsTeamMemberPositionEnum
    | null
    | undefined
): string => {
  if (
    !emsTeamMemberPosition ||
    !(emsTeamMemberPosition in EMS_TEAM_MEMBER_POSITION_LABELS)
  ) {
    return '不明';
  }

  return EMS_TEAM_MEMBER_POSITION_LABELS[emsTeamMemberPosition];
};

/**
 * 傷病者の性別の日本語ラベルマッピング
 */
const PATIENT_SEX_LABELS = {
  [BaseInfoSexEnum.MALE]: '男',
  [BaseInfoSexEnum.FEMALE]: '女',
  [BaseInfoSexEnum.UNKNOWN]: '不明',
} as const satisfies Record<keyof typeof BaseInfoSexEnum, string>;

/**
 * 傷病者の性別の日本語ラベルを取得する関数
 *
 * @param patientSex - 傷病者の性別
 * @returns 日本語ラベル
 */
export const getPatientSexLabel = (
  patientSex: keyof typeof BaseInfoSexEnum | null | undefined
): string => {
  if (!patientSex || !(patientSex in PATIENT_SEX_LABELS)) {
    return '不明';
  }

  return PATIENT_SEX_LABELS[patientSex];
};

/**
 * 職業の日本語ラベルマッピング
 */
const OCCUPATION_LABELS = {
  [BaseInfoOccupationEnum.UNEMPLOYED]: '無職',
  [BaseInfoOccupationEnum.EMPLOYEE]: '会社員',
  [BaseInfoOccupationEnum.GOVERNMENT]: '公務員',
  [BaseInfoOccupationEnum.NONPROFIT]: '団体職',
  [BaseInfoOccupationEnum.SELF]: '自営業',
  [BaseInfoOccupationEnum.MEDICAL]: '医療関係',
  [BaseInfoOccupationEnum.SERVICE_WORKER]: 'サービス業',
  [BaseInfoOccupationEnum.STUDENT]: '学生',
  [BaseInfoOccupationEnum.ELEMENTARY_SCHOOL_STUDENT]: '小学生',
  [BaseInfoOccupationEnum.MIDDLE_SCHOOL_STUDENT]: '中学生',
  [BaseInfoOccupationEnum.HIGH_SCHOOL_STUDENT]: '高校生',
  [BaseInfoOccupationEnum.TECHNICAL_COLLEGE_STUDENT]: '高等専門学校生',
  [BaseInfoOccupationEnum.JUNIOR_COLLEGE_STUDENT]: '短大生',
  [BaseInfoOccupationEnum.UNIVERSITY_STUDENT]: '大学生',
  [BaseInfoOccupationEnum.VOCATIONAL_SCHOOL_STUDENT]: '各種学校生',
  [BaseInfoOccupationEnum.FREELANCER]: '自由業',
  [BaseInfoOccupationEnum.MAFF]: '農林漁業',
  [BaseInfoOccupationEnum.LABORER]: '労働者(日雇い等)',
  [BaseInfoOccupationEnum.PART_TIME_WORKER]: 'パート・アルバイト',
  [BaseInfoOccupationEnum.CHILD]: '児童',
  [BaseInfoOccupationEnum.INFANT]: '乳幼児',
  [BaseInfoOccupationEnum.HOUSEWIFE]: '主婦',
  [BaseInfoOccupationEnum.HOMEMAKER]: '主夫・主婦',
  [BaseInfoOccupationEnum.OTHER]: 'その他',
  [BaseInfoOccupationEnum.UNKNOWN]: '不明',
} as const satisfies Record<keyof typeof BaseInfoOccupationEnum, string>;

/**
 * 職業の日本語ラベルを取得する関数
 *
 * @param occupation - 職業
 * @returns 日本語ラベル
 */
export const getOccupationLabel = (
  occupation: keyof typeof BaseInfoOccupationEnum | null | undefined
): string => {
  if (!occupation || !(occupation in OCCUPATION_LABELS)) {
    return '不明';
  }
  return OCCUPATION_LABELS[occupation];
};

/**
 * 傷病者とその関係者の関係の日本語ラベルマッピング
 */
const BASE_INFO_RELATED_PERSON_RELATIONSHIP_LABELS = {
  [BaseInfoRelatedPersonRelationshipEnum.FAMILY]: '家族',
  [BaseInfoRelatedPersonRelationshipEnum.RELATED]: '関係者',
  [BaseInfoRelatedPersonRelationshipEnum.DOCTOR]: '医師',
  [BaseInfoRelatedPersonRelationshipEnum.POLICEMAN]: '警察官',
} as const satisfies Record<
  keyof typeof BaseInfoRelatedPersonRelationshipEnum,
  string
>;

/**
 * 傷病者とその関係者の関係の日本語ラベルを取得する関数
 *
 * @param relationship - 傷病者とその関係者の関係
 * @returns 日本語ラベル
 */
export const getBaseInfoRelatedPersonRelationshipLabel = (
  relationship:
    | keyof typeof BaseInfoRelatedPersonRelationshipEnum
    | null
    | undefined
): string => {
  if (
    !relationship ||
    !(relationship in BASE_INFO_RELATED_PERSON_RELATIONSHIP_LABELS)
  ) {
    return '';
  }
  return BASE_INFO_RELATED_PERSON_RELATIONSHIP_LABELS[relationship];
};

/**
 * 傷病者とその関係者の同行者到着タイプの日本語ラベルマッピング
 */
const BASE_INFO_RELATED_PERSON_ATTENDANT_ARRIVAL_TYPE_LABELS = {
  [BaseInfoRelatedPersonAttendantArrivalTypeEnum.FOLLOW_UP]: '後追い',
  [BaseInfoRelatedPersonAttendantArrivalTypeEnum.RIDE_ALONG]: '同乗',
} as const satisfies Record<
  keyof typeof BaseInfoRelatedPersonAttendantArrivalTypeEnum,
  string
>;

/**
 * 傷病者とその関係者の同行者到着タイプの日本語ラベルを取得する関数
 *
 * @param arrivalType - 傷病者とその関係者の同行者到着タイプ
 * @returns 日本語ラベル
 */
export const getBaseInfoRelatedPersonAttendantArrivalTypeLabel = (
  arrivalType:
    | keyof typeof BaseInfoRelatedPersonAttendantArrivalTypeEnum
    | null
    | undefined
): string => {
  if (
    !arrivalType ||
    !(arrivalType in BASE_INFO_RELATED_PERSON_ATTENDANT_ARRIVAL_TYPE_LABELS)
  ) {
    return '';
  }
  return BASE_INFO_RELATED_PERSON_ATTENDANT_ARRIVAL_TYPE_LABELS[arrivalType];
};

/**
 * 傷病者の預かり品の日本語ラベルマッピング
 */
const BASE_INFO_CUSTODY_ITEM_LABELS = {
  [BaseInfoCustodyItemEnum.PRESCRIPTION_RECORD]: '薬手帳',
} as const satisfies Record<keyof typeof BaseInfoCustodyItemEnum, string>;

/**
 * 傷病者の預かり品の日本語ラベルを取得する関数
 *
 * @param item - 傷病者の預かり品
 * @returns 日本語ラベル
 */
export const getBaseInfoCustodyItemLabel = (
  item: keyof typeof BaseInfoCustodyItemEnum | null | undefined
): string => {
  if (!item || !(item in BASE_INFO_CUSTODY_ITEM_LABELS)) {
    return '';
  }
  return BASE_INFO_CUSTODY_ITEM_LABELS[item];
};

/**
 * 傷病者の連携タイミングの日本語ラベルマッピング
 */
const BASE_INFO_COLLABORATION_TIMING_LABELS = {
  [BaseInfoCollaborationTimingEnum.BEFORE_CONTACT]: '接触前',
  [BaseInfoCollaborationTimingEnum.AFTER_CONTACT]: '接触後',
} as const satisfies Record<
  keyof typeof BaseInfoCollaborationTimingEnum,
  string
>;

/**
 * 傷病者の連携タイミングの日本語ラベルを取得する関数
 *
 * @param timing - 傷病者の連携タイミング
 * @returns 日本語ラベル
 */
export const getBaseInfoCollaborationTimingLabel = (
  timing: keyof typeof BaseInfoCollaborationTimingEnum | null | undefined
): string => {
  if (!timing || !(timing in BASE_INFO_COLLABORATION_TIMING_LABELS)) {
    return '';
  }
  return BASE_INFO_COLLABORATION_TIMING_LABELS[timing];
};

/**
 * 傷病者の連携先種別の日本語ラベルマッピング
 */
const BASE_INFO_COLLABORATION_LABELS = {
  [BaseInfoCollaborationEnum.OTHER_EMS_TEAM]: '他救急隊',
  [BaseInfoCollaborationEnum.FIREFIGHTING_TEAM]: '消防隊',
  [BaseInfoCollaborationEnum.RESCUE_TEAM]: '救急隊',
  [BaseInfoCollaborationEnum.DR_CAR]: 'DrCar',
  [BaseInfoCollaborationEnum.DR_HELI]: 'DrHeli',
  [BaseInfoCollaborationEnum.DISASTER_DR_HELI]: '防災Heli',
  [BaseInfoCollaborationEnum.OTHER]: '他',
} as const satisfies Record<keyof typeof BaseInfoCollaborationEnum, string>;

/**
 * 傷病者の連携先種別の日本語ラベルを取得する関数
 *
 * @param collaboration - 傷病者の連携先種別
 * @returns 日本語ラベル
 */
export const getBaseInfoCollaborationLabel = (
  collaboration: keyof typeof BaseInfoCollaborationEnum | null | undefined
): string => {
  if (!collaboration || !(collaboration in BASE_INFO_COLLABORATION_LABELS)) {
    return '';
  }
  return BASE_INFO_COLLABORATION_LABELS[collaboration];
};

/**
 * 傷病者の既往歴の日本語ラベルマッピング
 */
const REQUEST_SUMMARY_MEDICAL_HISTORY_LABELS = {
  [RequestSummaryMedicalHistoryEnum.HYPER_TENSION]: '高血圧',
  [RequestSummaryMedicalHistoryEnum.HYPER_LIPIDEMIA]: '高脂血症',
  [RequestSummaryMedicalHistoryEnum.COPD]: 'COPD',
  [RequestSummaryMedicalHistoryEnum.DIABETES]: '糖尿病',
  [RequestSummaryMedicalHistoryEnum.MYOCARDIAL_INFARCTION]: '心筋梗塞',
  [RequestSummaryMedicalHistoryEnum.CANCER]: '悪性腫瘍',
  [RequestSummaryMedicalHistoryEnum.CEREBRAL_INFARCTION]: '脳梗塞',
  [RequestSummaryMedicalHistoryEnum.DEMENTIA]: '認知症',
  [RequestSummaryMedicalHistoryEnum.EPILEPSY]: 'てんかん',
  [RequestSummaryMedicalHistoryEnum.ANGINA]: '狭心症',
  [RequestSummaryMedicalHistoryEnum.KIDNEY_FAILURE]: '腎不全',
  [RequestSummaryMedicalHistoryEnum.HEART_FAILURE]: '心不全',
  [RequestSummaryMedicalHistoryEnum.DIALYSIS]: '透析',
  [RequestSummaryMedicalHistoryEnum.MENTAL_ILLNESS]: '精神病',
  [RequestSummaryMedicalHistoryEnum.ASTHMA]: '喘息',
  [RequestSummaryMedicalHistoryEnum.ARRHYTHMIA]: '不整脈',
  [RequestSummaryMedicalHistoryEnum.OTHER_HEART_DISEASES]: '他の心疾患',
  [RequestSummaryMedicalHistoryEnum.CEREBROVASCULAR]: '脳血管',
  [RequestSummaryMedicalHistoryEnum.RESPIRATORY_DISEASES]: '呼吸器',
  [RequestSummaryMedicalHistoryEnum.LIVER_DISEASES]: '肝疾患',
  [RequestSummaryMedicalHistoryEnum.KIDNEY_DISEASES]: '腎疾患',
  [RequestSummaryMedicalHistoryEnum.DIGESTIVE_DISEASES]: '消化器',
  [RequestSummaryMedicalHistoryEnum.CEREBRAL_HEMORRHAGE]: '脳出血',
  [RequestSummaryMedicalHistoryEnum.CIRCULATORY_SYSTEM]: '循環器疾患',
  [RequestSummaryMedicalHistoryEnum.NERVOUS_SYSTEM]: '神経系疾患',
  [RequestSummaryMedicalHistoryEnum.ENDOCRINE_SYSTEM]: '内分泌系疾患',
  [RequestSummaryMedicalHistoryEnum.HEMATOLOGY]: '血液疾患',
  [RequestSummaryMedicalHistoryEnum.OBSTETRICS_AND_GYNECOLOGY]: '婦人科疾患',
  [RequestSummaryMedicalHistoryEnum.INFECTIOUS_DISEASES]: '感染症',
  [RequestSummaryMedicalHistoryEnum.RENAL_UROLOGY]: '腎・泌尿器疾患',
  [RequestSummaryMedicalHistoryEnum.ALLERGY]: 'アレルギー',
  [RequestSummaryMedicalHistoryEnum.OTHER]: 'その他',
} as const satisfies Record<
  keyof typeof RequestSummaryMedicalHistoryEnum,
  string
>;

/**
 * 傷病者の既往歴の日本語ラベルを取得する関数
 *
 * @param medicalHistory - 傷病者の既往歴
 * @returns 日本語ラベル
 */
export const getRequestSummaryMedicalHistoryLabel = (
  medicalHistory:
    | keyof typeof RequestSummaryMedicalHistoryEnum
    | null
    | undefined
): string => {
  if (
    !medicalHistory ||
    !(medicalHistory in REQUEST_SUMMARY_MEDICAL_HISTORY_LABELS)
  ) {
    return '';
  }
  return REQUEST_SUMMARY_MEDICAL_HISTORY_LABELS[medicalHistory];
};

/**
 * 傷病者の日常生活能力の日本語ラベルマッピング
 */
const REQUEST_SUMMARY_ADL_LABELS = {
  [RequestSummaryADLEnum.INDEPENDENT]: '自立',
  [RequestSummaryADLEnum.CARE_REQUIRED]: '介護あり',
  [RequestSummaryADLEnum.BEDRIDDEN]: '寝たきり',
  [RequestSummaryADLEnum.PARTIAL_ASSISTANCE]: '一部介助',
  [RequestSummaryADLEnum.FULL_ASSISTANCE]: '全介助',
  [RequestSummaryADLEnum.UNKNOWN]: '不明',
} as const satisfies Record<keyof typeof RequestSummaryADLEnum, string>;

/**
 * 傷病者の日常生活能力の日本語ラベルを取得する関数
 *
 * @param adlLevel - 傷病者の日常生活能力
 * @returns 日本語ラベル
 */
export const getRequestSummaryADLLabel = (
  adlLevel: keyof typeof RequestSummaryADLEnum | null | undefined
): string => {
  if (!adlLevel || !(adlLevel in REQUEST_SUMMARY_ADL_LABELS)) {
    return '';
  }
  return REQUEST_SUMMARY_ADL_LABELS[adlLevel];
};

/**
 * 傷病者の日常生活能力レベルの日本語ラベルマッピング
 */
const REQUEST_SUMMARY_ADL_LEVEL_LABELS = {
  [RequestSummaryADLLevelEnum.FIRST]: '1',
  [RequestSummaryADLLevelEnum.SECOND]: '2',
  [RequestSummaryADLLevelEnum.THIRD]: '3',
  [RequestSummaryADLLevelEnum.FOURTH]: '4',
  [RequestSummaryADLLevelEnum.FIFTH]: '5',
  [RequestSummaryADLLevelEnum.UNKNOWN]: '不明',
} as const satisfies Record<keyof typeof RequestSummaryADLLevelEnum, string>;

/**
 * 傷病者の日常生活能力レベルの日本語ラベルを取得する関数
 *
 * @param adlLevel - 傷病者の日常生活能力レベル
 * @returns 日本語ラベル
 */
export const getRequestSummaryADLLevelLabel = (
  adlLevel: keyof typeof RequestSummaryADLLevelEnum | null | undefined
): string => {
  if (!adlLevel || !(adlLevel in REQUEST_SUMMARY_ADL_LEVEL_LABELS)) {
    return '';
  }
  return REQUEST_SUMMARY_ADL_LEVEL_LABELS[adlLevel];
};

/**
 * 傷病者の発症タイプの日本語ラベルマッピング
 */
const REQUEST_SUMMARY_ONSET_TYPE_LABELS = {
  [RequestSummaryOnsetTypeEnum.ESTIMATED]: '推',
  [RequestSummaryOnsetTypeEnum.CONFIRMED]: '確',
  [RequestSummaryOnsetTypeEnum.UNKNOWN]: '不明',
} as const satisfies Record<keyof typeof RequestSummaryOnsetTypeEnum, string>;

/**
 * 傷病者の発症タイプの日本語ラベルを取得する関数
 *
 * @param onsetType - 傷病者の発症タイプ
 * @returns 日本語ラベル
 */
export const getRequestSummaryOnsetTypeLabel = (
  onsetType: keyof typeof RequestSummaryOnsetTypeEnum | null | undefined
): string => {
  if (!onsetType || !(onsetType in REQUEST_SUMMARY_ONSET_TYPE_LABELS)) {
    return '';
  }
  return REQUEST_SUMMARY_ONSET_TYPE_LABELS[onsetType];
};

/**
 * 測定タイミングの日本語ラベルマッピング
 */
const MEASURE_TIMING_LABELS = {
  [MeasureTimingEnum.CONTACT]: '接触時',
  // TODO: 対応する日本語が不明あとで確認する
  [MeasureTimingEnum.SITE]: '現場',
  [MeasureTimingEnum.AMBULANCE_ACCOMMODATION]: '車内収容時',
  [MeasureTimingEnum.IN_AMBULANCE]: '車内',
  [MeasureTimingEnum.TRANSPORTING]: '搬送中',
  [MeasureTimingEnum.HOSPITAL_ARRIVAL]: '病着時',
  [MeasureTimingEnum.BEFORE_HOSPITAL_ARRIVAL]: '',
  [MeasureTimingEnum.SITE_DEPARTURE]: '現発時',
  [MeasureTimingEnum.OTHER]: 'その他',
} as const satisfies Record<keyof typeof MeasureTimingEnum, string>;

/**
 * 測定タイミングの日本語ラベルを取得する関数
 *
 * @param measureTiming - 測定タイミング
 * @returns 日本語ラベル
 */
export const getMeasureTimingLabel = (
  measureTiming: keyof typeof MeasureTimingEnum | null | undefined
): string => {
  if (!measureTiming || !(measureTiming in MEASURE_TIMING_LABELS)) {
    return '';
  }
  return MEASURE_TIMING_LABELS[measureTiming];
};

/**
 * 交通事故状況の日本語ラベルマッピング
 */
const TRAFFIC_ACCIDENT_SITUATION_LABELS = {
  [TrafficAccidentSituationEnum.VEHICLE]: '自動車',
  [TrafficAccidentSituationEnum.MOTORCYCLE]: 'バイク',
  [TrafficAccidentSituationEnum.BICYCLE]: '自転車',
  [TrafficAccidentSituationEnum.PEDESTRIAN]: '歩行者',
} as const satisfies Record<keyof typeof TrafficAccidentSituationEnum, string>;

/**
 * 交通事故状況の日本語ラベルを取得する関数
 *
 * @param situation - 交通事故状況
 * @returns 日本語ラベル
 */
export const getTrafficAccidentSituationLabel = (
  situation: keyof typeof TrafficAccidentSituationEnum | null | undefined
): string => {
  if (!situation || !(situation in TRAFFIC_ACCIDENT_SITUATION_LABELS)) {
    return '';
  }
  return TRAFFIC_ACCIDENT_SITUATION_LABELS[situation];
};

/**
 * 交通事故事故タイプの日本語ラベルマッピング
 */
const TRAFFIC_ACCIDENT_ACCIDENT_TYPE_LABELS = {
  [TrafficAccidentAccidentTypeEnum.HEAD_ON_COLLISION]: '正面衝突',
  [TrafficAccidentAccidentTypeEnum.SIDE_COLLISION]: '側面衝突',
  [TrafficAccidentAccidentTypeEnum.REAR_END_COLLISION]: '追突',
  [TrafficAccidentAccidentTypeEnum.SINGLE_VEHICLE]: '自損',
} as const satisfies Record<
  keyof typeof TrafficAccidentAccidentTypeEnum,
  string
>;

/**
 * 交通事故事故タイプの日本語ラベルを取得する関数
 *
 * @param accidentType - 交通事故事故タイプ
 * @returns 日本語ラベル
 */
export const getTrafficAccidentAccidentTypeLabel = (
  accidentType: keyof typeof TrafficAccidentAccidentTypeEnum | null | undefined
): string => {
  if (
    !accidentType ||
    !(accidentType in TRAFFIC_ACCIDENT_ACCIDENT_TYPE_LABELS)
  ) {
    return '';
  }
  return TRAFFIC_ACCIDENT_ACCIDENT_TYPE_LABELS[accidentType];
};

/**
 * ドクターカー/ドクターヘリ出動の日本語ラベルマッピング
 */
const DR_CAR_DR_HELI_DISPATCH_LABELS = {
  [DrCarDrHeliDispatchEnum.DISPATCHED]: 'あり',
  [DrCarDrHeliDispatchEnum.NONE]: 'なし',
  [DrCarDrHeliDispatchEnum.CANCELED_IN_ROUTE]: '途中引揚げ',
} as const satisfies Record<keyof typeof DrCarDrHeliDispatchEnum, string>;

/**
 * ドクターカー/ドクターヘリ出動の日本語ラベルを取得する関数
 *
 * @param dispatch - ドクターカー/ドクターヘリ出動
 * @returns 日本語ラベル
 */
export const getDrCarDrHeliDispatchLabel = (
  dispatch: keyof typeof DrCarDrHeliDispatchEnum | null | undefined
): string => {
  if (!dispatch || !(dispatch in DR_CAR_DR_HELI_DISPATCH_LABELS)) {
    return '';
  }
  return DR_CAR_DR_HELI_DISPATCH_LABELS[dispatch];
};

/**
 * 実施基準.対象の日本語ラベルマッピング
 */
const OPERATION_CRITERIA_DETAIL_LABELS = {
  [OperationCriteriaDetailEnum.CRITICAL]: '重篤',
  [OperationCriteriaDetailEnum.MYOCARDIAL_INFARCTION]: '心筋梗塞',
  [OperationCriteriaDetailEnum.STROKE]: '脳梗塞',
  [OperationCriteriaDetailEnum.SEVERE_TRAUMA]: '重症外傷',
  [OperationCriteriaDetailEnum.PREGNANT]: '妊産婦',
  [OperationCriteriaDetailEnum.PEDIATRIC]: '小児',
} as const satisfies Record<keyof typeof OperationCriteriaDetailEnum, string>;

/**
 * 実施基準.対象の日本語ラベルを取得する関数
 *
 * @param operationCriteriaDetail - 実施基準.対象
 * @returns 日本語ラベル
 */
export const getOperationCriteriaDetailLabel = (
  operationCriteriaDetail:
    | keyof typeof OperationCriteriaDetailEnum
    | null
    | undefined
): string => {
  if (
    !operationCriteriaDetail ||
    !(operationCriteriaDetail in OPERATION_CRITERIA_DETAIL_LABELS)
  ) {
    return '';
  }
  return OPERATION_CRITERIA_DETAIL_LABELS[operationCriteriaDetail];
};

/**
 * 酸素投与方法(器具)の日本語ラベルマッピング
 */
const VITAL_O2_MEAN_LABELS = {
  [VitalO2MeanEnum.NASAL_CANNULA]: '鼻カニューレ',
  [VitalO2MeanEnum.FACE_MASK]: 'フェイスマスク',
  [VitalO2MeanEnum.VENTURI]: 'ベンチュリー',
  [VitalO2MeanEnum.RESERVOIR_MASK]: 'リザーバーマスク',
  [VitalO2MeanEnum.BVM]: 'BVM',
  [VitalO2MeanEnum.VENTILATOR]: 'ベンチレーター',
  [VitalO2MeanEnum.OTHER]: 'その他',
  [VitalO2MeanEnum.NONE]: 'なし',
} as const satisfies Record<keyof typeof VitalO2MeanEnum, string>;

/**
 * 酸素投与方法(器具)の日本語ラベルを取得する関数
 *
 * @param o2Mean - 酸素投与方法(器具)
 * @returns 日本語ラベル
 */
export const getVitalO2MeanLabel = (
  o2Mean: keyof typeof VitalO2MeanEnum | null | undefined
): string => {
  if (!o2Mean || !(o2Mean in VITAL_O2_MEAN_LABELS)) {
    return '';
  }
  return VITAL_O2_MEAN_LABELS[o2Mean];
};

/**
 * 体温測定部位の日本語ラベルマッピング
 */
const VITAL_BT_PART_LABELS = {
  [VitalBtPartEnum.AXILLA]: '腋窩',
  [VitalBtPartEnum.TYMPANIC_MEMBRANE]: '鼓膜',
  [VitalBtPartEnum.NON_CONTACT]: '非接触',
} as const satisfies Record<keyof typeof VitalBtPartEnum, string>;

/**
 * 体温測定部位の日本語ラベルを取得する関数
 *
 * @param btPart - 体温測定部位
 * @returns 日本語ラベル
 */
export const getVitalBtPartLabel = (
  btPart: keyof typeof VitalBtPartEnum | null | undefined
): string => {
  if (!btPart || !(btPart in VITAL_BT_PART_LABELS)) {
    return '';
  }
  return VITAL_BT_PART_LABELS[btPart];
};

/**
 * 血圧測定部位の日本語ラベルマッピング
 */
const VITAL_BP_PART_LABELS = {
  [VitalBpPartEnum.RADIAL]: '橈骨',
  [VitalBpPartEnum.FEMORAL]: '大腿',
  [VitalBpPartEnum.COMMON_CAROTID]: '頸動脈',
} as const satisfies Record<keyof typeof VitalBpPartEnum, string>;

/**
 * 血圧測定部位の日本語ラベルを取得する関数
 *
 * @param bpPart - 血圧測定部位
 * @param isUnpalpable - 触知不能かどうか
 * @returns 日本語ラベル
 */
export const getVitalBpPartLabel = (
  bpPart: keyof typeof VitalBpPartEnum | null | undefined,
  isUnpalpable: boolean | null | undefined
): string => {
  if (isUnpalpable) return '触知不能';

  if (!bpPart || !(bpPart in VITAL_BP_PART_LABELS)) {
    return '';
  }

  return VITAL_BP_PART_LABELS[bpPart];
};

/**
 * 酸素投与条件の日本語ラベルマッピング
 */
const VITAL_O2_FLOW_CONDITION_LABELS = {
  [VitalO2FlowConditionEnum.NONE]: '投与なし',
  [VitalO2FlowConditionEnum.MASK]: 'Mask',
  [VitalO2FlowConditionEnum.RM]: 'RM',
  [VitalO2FlowConditionEnum.OTHER]: '他',
} as const satisfies Record<keyof typeof VitalO2FlowConditionEnum, string>;

/**
 * 酸素投与条件の日本語ラベルを取得する関数
 *
 * @param o2FlowCondition - 酸素投与条件
 * @returns 日本語ラベル
 */
export const getVitalO2FlowConditionLabel = (
  o2FlowCondition: keyof typeof VitalO2FlowConditionEnum | null | undefined
): string => {
  if (
    !o2FlowCondition ||
    !(o2FlowCondition in VITAL_O2_FLOW_CONDITION_LABELS)
  ) {
    return '';
  }
  return VITAL_O2_FLOW_CONDITION_LABELS[o2FlowCondition];
};

/**
 * 心電図波形の日本語ラベルマッピング
 */
const ECG_WAVEFORM_LABELS = {
  [ECGWaveformEnum.NONE]: '',
  [ECGWaveformEnum.NORMAL]: '正常',
  [ECGWaveformEnum.SINUS_RHYTHM]: '洞調律',
  [ECGWaveformEnum.SINUS_BRADYCARDIA]: '洞徐脈',
  [ECGWaveformEnum.SINUS_TACHYCARDIA]: '洞頻脈',
  [ECGWaveformEnum.ASYSTOLE]: '心静止',
  [ECGWaveformEnum.ARRHYTHMIA]: '心律不整',
  [ECGWaveformEnum.PEA]: 'PEA',
  [ECGWaveformEnum.VENTRICULAR_FIBRILLATION]: '心室細動',
  [ECGWaveformEnum.VENTRICULAR_TACHYCARDIA]: '心室頻拍',
  [ECGWaveformEnum.PULSELESS_VT]: '無脈性VT',
  [ECGWaveformEnum.ATRIAL_FIBRILLATION]: '心房細動',
  [ECGWaveformEnum.ATRIAL_FLUTTER]: '心房粗動',
  [ECGWaveformEnum.PSVT]: '上室性頻拍',
  [ECGWaveformEnum.AV_BLOCK]: '房室ブロック',
  [ECGWaveformEnum.FIRST_DEGREE_AV_BLOCK]: 'Ⅰ度房室ブロック',
  [ECGWaveformEnum.SECOND_DEGREE_AV_BLOCK]: 'Ⅱ度房室ブロック',
  [ECGWaveformEnum.THIRD_DEGREE_AV_BLOCK]: 'Ⅲ度房室ブロック',
  [ECGWaveformEnum.BUNDLE_BRANCH_BLOCK]: 'ブンドルブランチブロック',
  [ECGWaveformEnum.BRADYCARDIA]: '徐脈性不整脈',
  [ECGWaveformEnum.TACHYCARDIA]: '頻脈性不整脈',
  [ECGWaveformEnum.ST_ELEVATION]: 'ST上昇',
  [ECGWaveformEnum.ST_DEPRESSION]: 'ST低下',
  [ECGWaveformEnum.PVC]: '心室性期外収縮',
  [ECGWaveformEnum.VPC]: '心室性期外収縮',
  [ECGWaveformEnum.OTHER]: 'その他',
} as const satisfies Record<keyof typeof ECGWaveformEnum, string>;

/**
 * 心電図波形の日本語ラベルを取得する関数
 *
 * @param ecgWaveform - 心電図波形
 * @returns 日本語ラベル
 */
export const getECGWaveformLabel = (
  ecgWaveform: keyof typeof ECGWaveformEnum | null | undefined
): string => {
  if (!ecgWaveform || !(ecgWaveform in ECG_WAVEFORM_LABELS)) {
    return '';
  }
  return ECG_WAVEFORM_LABELS[ecgWaveform];
};

/**
 * 心電図補足の日本語ラベルマッピング
 */
const ECG_WAVE_REMARKS_LABELS = {
  [ECGWaveRemarksEnum.ST_ELEVATION]: 'ST上昇',
  [ECGWaveRemarksEnum.ST_DEPRESSION]: 'ST低下',
  [ECGWaveRemarksEnum.PVC]: '心室性期外収縮',
  [ECGWaveRemarksEnum.PAC]: '上室性期外収縮',
  [ECGWaveRemarksEnum.OTHER]: 'その他',
} as const satisfies Record<keyof typeof ECGWaveRemarksEnum, string>;

/**
 * 心電図補足の日本語ラベルを取得する関数
 *
 * @param ecgWaveRemarks - 心電図補足
 * @returns 日本語ラベル
 */
export const getECGWaveRemarksLabel = (
  ecgWaveRemarks: keyof typeof ECGWaveRemarksEnum | null | undefined
): string => {
  if (!ecgWaveRemarks || !(ecgWaveRemarks in ECG_WAVE_REMARKS_LABELS)) {
    return '';
  }
  return ECG_WAVE_REMARKS_LABELS[ecgWaveRemarks];
};

/**
 * 瞳孔対光反射の日本語ラベルマッピング
 */
const PUPIL_REACTION_LABELS = {
  [PupilReactionEnum.POSITIVE]: '速',
  [PupilReactionEnum.SLUGGISH]: '鈍',
  [PupilReactionEnum.NEGATIVE]: '無',
  [PupilReactionEnum.UNKNOWN]: '不明',
} as const satisfies Record<keyof typeof PupilReactionEnum, string>;

/**
 * 瞳孔対光反射の日本語ラベルを取得する関数
 *
 * @param pupilReaction - 瞳孔対光反射
 * @returns 日本語ラベル
 */
export const getPupilReactionLabel = (
  pupilReaction: keyof typeof PupilReactionEnum | null | undefined
): string => {
  if (!pupilReaction || !(pupilReaction in PUPIL_REACTION_LABELS)) {
    return '';
  }
  return PUPIL_REACTION_LABELS[pupilReaction];
};

/**
 * 気管挿管装置の日本語ラベルマッピング
 */
const INTUBATION_DEVICE_LABELS = {
  [IntubationDevice.MANUAL]: '用手法',
  [IntubationDevice.NASAL_AIRWAY]: '経鼻エアウェイ',
  [IntubationDevice.LT]: 'LT',
  [IntubationDevice.TRACHEAL_INTUBATION]: '気管挿管',
  [IntubationDevice.VIDEO_LARYNGOSCOPE]: 'ビデオ喉頭鏡',
  [IntubationDevice.FOREIGN_BODY_REMOVAL]: '異物除去',
  [IntubationDevice.OTHER]: 'その他',
  // 以下は宇部では利用しない
  [IntubationDevice.LM]: '',
} as const satisfies Record<keyof typeof IntubationDevice, string>;

/**
 * 気管挿管装置の日本語ラベルを取得する関数
 *
 * @param intubationDevice - 気管挿管装置
 * @returns 日本語ラベル
 */
export const getIntubationDeviceLabel = (
  intubationDevice: keyof typeof IntubationDevice | null | undefined
): string => {
  if (!intubationDevice || !(intubationDevice in INTUBATION_DEVICE_LABELS)) {
    return '';
  }
  return INTUBATION_DEVICE_LABELS[intubationDevice];
};

/**
 * 静脈路適応の日本語ラベルを取得する関数
 *
 * @param ivAccessTiming - 静脈路適応
 * @returns 日本語ラベル
 */
const TREATMENT_IV_ACCESS_TIMING_LABELS = {
  [TreatmentIvAccessTimingEnum.PRE_CPA]: 'CPA前',
  [TreatmentIvAccessTimingEnum.POST_CPA]: 'CPA後',
  [TreatmentIvAccessTimingEnum.GLUCOSE]: 'ブドウ糖',
  [TreatmentIvAccessTimingEnum.CPA]: 'CPA',
  [TreatmentIvAccessTimingEnum.SHOCK]: 'ショック',
  [TreatmentIvAccessTimingEnum.CRASH]: 'クラッシュ',
} as const satisfies Record<keyof typeof TreatmentIvAccessTimingEnum, string>;

/**
 * 静脈路適応の日本語ラベルを取得する関数
 *
 * @param ivAccessTiming - 静脈路適応
 * @returns 日本語ラベル
 */
export const getTreatmentIvAccessTimingLabel = (
  ivAccessTiming: keyof typeof TreatmentIvAccessTimingEnum | null | undefined
): string => {
  if (
    !ivAccessTiming ||
    !(ivAccessTiming in TREATMENT_IV_ACCESS_TIMING_LABELS)
  ) {
    return '';
  }
  return TREATMENT_IV_ACCESS_TIMING_LABELS[ivAccessTiming];
};

/**
 * 投与薬物の日本語ラベルマッピング
 */
const ADMINISTERED_MEDICATION_LABELS = {
  [AdministeredMedication.ADRENALINE]: 'アドレナリン',
  [AdministeredMedication.GLUCOSE]: 'ブドウ糖',
  [AdministeredMedication.EPINEPHRINE]: 'エピペン',
} as const satisfies Record<keyof typeof AdministeredMedication, string>;

/**
 * 投与薬物の日本語ラベルを取得する関数
 *
 * @param administeredMedication - 投与薬物
 * @returns 日本語ラベル
 */
export const getAdministeredMedicationLabel = (
  administeredMedication: keyof typeof AdministeredMedication | null | undefined
): string => {
  if (
    !administeredMedication ||
    !(administeredMedication in ADMINISTERED_MEDICATION_LABELS)
  ) {
    return '';
  }
  return ADMINISTERED_MEDICATION_LABELS[administeredMedication];
};

/**
 * 最終食事の日本語ラベルを取得する関数
 *
 * @param isLastMeal - 最終食事の有無
 * @returns 日本語ラベル
 */
export const getRequestSummaryIsLastMealLabel = (
  isLastMeal: boolean | null | undefined
): string => {
  if (!isLastMeal) {
    return '不明';
  }
  return '特定';
};

/**
 * 安全装備の日本語ラベルマッピング
 */
const SAFETY_EQUIPMENTS_LABELS = {
  [FindEmsContactSafetyEquipmentsEnum.SEAT_BELT]: 'シートベルト',
  [FindEmsContactSafetyEquipmentsEnum.AIR_BAG]: 'エアバッグ',
  [FindEmsContactSafetyEquipmentsEnum.CHILD_SEAT]: 'チャイルドシート',
  [FindEmsContactSafetyEquipmentsEnum.HELMET]: 'ヘルメット',
} as const satisfies Record<
  keyof typeof FindEmsContactSafetyEquipmentsEnum,
  string
>;

/**
 * 安全装備の日本語ラベルを取得する関数
 *
 * @param safetyEquipment - 安全装備
 * @returns 日本語ラベル
 */
export const getSafetyEquipmentsLabel = (
  safetyEquipment:
    | keyof typeof FindEmsContactSafetyEquipmentsEnum
    | null
    | undefined
): string => {
  if (!safetyEquipment) {
    return '';
  }
  return SAFETY_EQUIPMENTS_LABELS[safetyEquipment];
};

/**
 * 傷病者の状況の日本語ラベルを取得する関数
 *
 * @param safetyEquipments - 安全装備
 * @param isHomeOxygen - 在宅酸素の有無
 * @param isOnDialysis - 透析の有無
 * @param isPregnant - 妊娠の有無
 * @returns 日本語ラベル
 */
export const getFindingsEmsContactConditionsLabel = (
  safetyEquipments: FindEmsContactSafetyEquipmentsEnum[] | null | undefined,
  isHomeOxygen: boolean | null | undefined,
  isOnDialysis: boolean | null | undefined,
  isPregnant: boolean | null | undefined
): string[] => {
  const conditions: string[] = [];

  // 安全装備
  if (safetyEquipments?.length) {
    conditions.push(...safetyEquipments.map(getSafetyEquipmentsLabel));
  }

  // その他の条件
  if (isHomeOxygen) conditions.push('在宅酸素');
  if (isOnDialysis) conditions.push('透析');
  if (isPregnant) conditions.push('妊娠');

  return conditions;
};

/**
 * JUST7の日本語ラベルマッピング
 */
const JUST7_LABELS = {
  [JUST7Enum.BP165]: '血圧≧165',
  [JUST7Enum.ARRHYTHMIA]: '不整脈',
  [JUST7Enum.CONJUGATE_DEVIATION]: '共同偏視',
  [JUST7Enum.HEADACHE]: '頭痛',
  [JUST7Enum.DYSARTHRIA]: '構音障害',
  [JUST7Enum.DISTURBANCE_OF_CONSCIOUSNESS]: '意識障害',
  [JUST7Enum.UPPER_LIMB_PARALYSIS]: '上肢麻痺',
} as const satisfies Record<keyof typeof JUST7Enum, string>;

/**
 * JUST7の日本語ラベルを取得する関数
 *
 * @param just7 - JUST7
 * @returns 日本語ラベル
 */
export const getJust7Label = (
  just7: keyof typeof JUST7Enum | null | undefined
): string => {
  if (!just7 || !(just7 in JUST7_LABELS)) {
    return '';
  }
  return JUST7_LABELS[just7];
};

/**
 * 麻痺部位の日本語ラベルマッピング
 */
const FIND_EMS_CONTACT_PARALYSIS_PARTS_LABELS = {
  [FindEmsContactParalysisPartsEnum.WHOLE_BODY]: '全身',
  [FindEmsContactParalysisPartsEnum.RIGHT_SIDE]: '右半身',
  [FindEmsContactParalysisPartsEnum.LEFT_SIDE]: '左半身',
  [FindEmsContactParalysisPartsEnum.UPPER_BODY]: '上半身',
  [FindEmsContactParalysisPartsEnum.LOWER_BODY]: '下半身',
  [FindEmsContactParalysisPartsEnum.RIGHT_UPPER_LIMB]: '右上肢',
  [FindEmsContactParalysisPartsEnum.LEFT_UPPER_LIMB]: '左上肢',
  [FindEmsContactParalysisPartsEnum.RIGHT_LOWER_LIMB]: '右下肢',
  [FindEmsContactParalysisPartsEnum.LEFT_LOWER_LIMB]: '左下肢',
} as const satisfies Record<
  keyof typeof FindEmsContactParalysisPartsEnum,
  string
>;

/**
 * 麻痺部位の日本語ラベルを取得する関数
 *
 * @param paralysisParts - 麻痺部位
 * @returns 日本語ラベル
 */
export const getFindEmsContactParalysisPartsLabel = (
  paralysisParts:
    | keyof typeof FindEmsContactParalysisPartsEnum
    | null
    | undefined
): string => {
  if (
    !paralysisParts ||
    !(paralysisParts in FIND_EMS_CONTACT_PARALYSIS_PARTS_LABELS)
  ) {
    return '';
  }
  return FIND_EMS_CONTACT_PARALYSIS_PARTS_LABELS[paralysisParts];
};

/**
 * 麻痺症状の日本語ラベルマッピング
 */
const FIND_EMS_CONTACT_PARALYSIS_SYMPTOMS_LABELS = {
  [FindEmsContactParalysisSymptomsEnum.COMPLETE]: '完全',
  [FindEmsContactParalysisSymptomsEnum.INCOMPLETE]: '不全',
  [FindEmsContactParalysisSymptomsEnum.SENSORY]: '知覚',
  [FindEmsContactParalysisSymptomsEnum.HYPESTHESIA]: '鈍麻',
  [FindEmsContactParalysisSymptomsEnum.NUMBNESS]: 'しびれ感',
  [FindEmsContactParalysisSymptomsEnum.SEVERE_SPEECH_DISORDER]: '言語障害重',
  [FindEmsContactParalysisSymptomsEnum.SPEECH]: '言語障害軽',
} as const satisfies Record<
  keyof typeof FindEmsContactParalysisSymptomsEnum,
  string
>;

/**
 * 麻痺症状の日本語ラベルを取得する関数
 *
 * @param paralysisSymptoms - 麻痺症状
 * @returns 日本語ラベル
 */
export const getFindEmsContactParalysisSymptomsLabel = (
  paralysisSymptoms:
    | keyof typeof FindEmsContactParalysisSymptomsEnum
    | null
    | undefined
): string => {
  if (
    !paralysisSymptoms ||
    !(paralysisSymptoms in FIND_EMS_CONTACT_PARALYSIS_SYMPTOMS_LABELS)
  ) {
    return '';
  }
  return FIND_EMS_CONTACT_PARALYSIS_SYMPTOMS_LABELS[paralysisSymptoms];
};

/**
 * 痙攣部位の日本語ラベルマッピング
 */
const FIND_EMS_CONTACT_SPASM_PARTS_LABELS = {
  [FindEmsContactSpasmPartsEnum.WHOLE_BODY]: '全身',
  [FindEmsContactSpasmPartsEnum.RIGHT_UPPER_LIMB]: '右上肢',
  [FindEmsContactSpasmPartsEnum.LEFT_UPPER_LIMB]: '左上肢',
  [FindEmsContactSpasmPartsEnum.RIGHT_LOWER_LIMB]: '右下肢',
  [FindEmsContactSpasmPartsEnum.LEFT_LOWER_LIMB]: '左下肢',
} as const satisfies Record<keyof typeof FindEmsContactSpasmPartsEnum, string>;

/**
 * 痙攣部位の日本語ラベルを取得する関数
 *
 * @param spasmParts - 痙攣部位
 * @returns 日本語ラベル
 */
export const getFindEmsContactSpasmPartsLabel = (
  spasmParts: keyof typeof FindEmsContactSpasmPartsEnum | null | undefined
): string => {
  if (!spasmParts || !(spasmParts in FIND_EMS_CONTACT_SPASM_PARTS_LABELS)) {
    return 'なし';
  }
  return FIND_EMS_CONTACT_SPASM_PARTS_LABELS[spasmParts];
};

/**
 * 出血部位の日本語ラベルマッピング
 */
const FIND_EMS_CONTACT_BLEEDING_TYPE_LABELS = {
  [FindEmsContactBleedingTypeEnum.NONE]: 'なし',
  [FindEmsContactBleedingTypeEnum.EXTERNAL_HEMORRHAGE]: '外出血',
  [FindEmsContactBleedingTypeEnum.HEMATEMESIS]: '吐血',
  [FindEmsContactBleedingTypeEnum.HEMOPTYSIS]: '喀血',
  [FindEmsContactBleedingTypeEnum.LOWER_GI_BLEEDING]: '下血',
  [FindEmsContactBleedingTypeEnum.GENITAL_HEMORRHAGE]: '性器出血',
  [FindEmsContactBleedingTypeEnum.SUBCUTANEOUS_HEMORRHAGE]: '皮下出血',
  [FindEmsContactBleedingTypeEnum.EPISTAXIS]: '鼻出血',
  [FindEmsContactBleedingTypeEnum.EXTERNAL_BLEEDING_ARTERIAL_HEAVY]:
    '外出血動脈性多量',
  [FindEmsContactBleedingTypeEnum.EXTERNAL_BLEEDING_ARTERIAL_LIGHT]:
    '外出血動脈性少量',
  [FindEmsContactBleedingTypeEnum.EXTERNAL_BLEEDING_VENOUS_HEAVY]:
    '外出血静脈性多量',
  [FindEmsContactBleedingTypeEnum.EXTERNAL_BLEEDING_VENOUS_LIGHT]:
    '外出血静脈性少量',
  [FindEmsContactBleedingTypeEnum.EXTERNAL_BLEEDING_CAPILLARY]: '外出血毛血管',
  [FindEmsContactBleedingTypeEnum.NOSEBLEED_HEAVY]: '鼻出血多量',
  [FindEmsContactBleedingTypeEnum.NOSEBLEED_LIGHT]: '鼻出血少量',
  [FindEmsContactBleedingTypeEnum.EAR_BLEEDING_HEAVY]: '耳出血多量',
  [FindEmsContactBleedingTypeEnum.EAR_BLEEDING_LIGHT]: '耳出血少量',
  [FindEmsContactBleedingTypeEnum.OTHER]: 'その他',
} as const satisfies Record<
  keyof typeof FindEmsContactBleedingTypeEnum,
  string
>;

/**
 * 出血部位の日本語ラベルを取得する関数
 *
 * @param bleedingType - 出血部位
 * @returns 日本語ラベル
 */
export const getFindEmsContactBleedingTypeLabel = (
  bleedingType: keyof typeof FindEmsContactBleedingTypeEnum | null | undefined
): string => {
  if (
    !bleedingType ||
    !(bleedingType in FIND_EMS_CONTACT_BLEEDING_TYPE_LABELS)
  ) {
    return '';
  }
  return FIND_EMS_CONTACT_BLEEDING_TYPE_LABELS[bleedingType];
};

/**
 * 一般治療の日本語ラベルマッピング
 */
const TREATMENT_GENERAL_TREATMENTS_LABELS = {
  [TreatmentGeneralTreatmentsEnum.MONITOR]: 'モニタ',
  [TreatmentGeneralTreatmentsEnum.ECG]: '12誘導',
  [TreatmentGeneralTreatmentsEnum.O2]: '酸素',
  [TreatmentGeneralTreatmentsEnum.SUCTION]: '吸引',
  [TreatmentGeneralTreatmentsEnum.FOREIGN_BODY_REMOVAL]: '異物除去',
  [TreatmentGeneralTreatmentsEnum.AIRWAY_ESTABLISHMENT_MANUAL]: '用手',
  [TreatmentGeneralTreatmentsEnum.AIRWAY_ESTABLISHMENT_BVM]: 'BVM',
  [TreatmentGeneralTreatmentsEnum.AIRWAY_ORAL]: 'エアウェイ（経口）',
  [TreatmentGeneralTreatmentsEnum.AIRWAY_NASAL]: 'エアウェイ（経鼻）',
  [TreatmentGeneralTreatmentsEnum.CHEST_COMPRESSION_MANUAL]: '胸骨圧迫（用手）',
  [TreatmentGeneralTreatmentsEnum.CHEST_COMPRESSION_AUTO]: '胸骨圧迫（自動）',
  [TreatmentGeneralTreatmentsEnum.HEMOSTASIS]: '止血',
  [TreatmentGeneralTreatmentsEnum.COVERING]: '被覆',
  [TreatmentGeneralTreatmentsEnum.WOUND_CARE]: '創処置',
  [TreatmentGeneralTreatmentsEnum.CERVICAL_COLLAR]: '頸椎カラー',
  [TreatmentGeneralTreatmentsEnum.SCOOP_STRETCHER]: '全身固定',
  [TreatmentGeneralTreatmentsEnum.PELVIC_STABILIZATION_SHEET]: 'シーツ',
  [TreatmentGeneralTreatmentsEnum.PELVIC_STABILIZATION_INSTRUMENT]: '器具',
  // 以下は宇部では利用しない
  [TreatmentGeneralTreatmentsEnum.DEFIBRILLATION]: '',
  [TreatmentGeneralTreatmentsEnum.ECG_TRANSMISSION]: '',
  [TreatmentGeneralTreatmentsEnum.AUSCULTATION]: '',
  [TreatmentGeneralTreatmentsEnum.O2_HIGH]: '',
  [TreatmentGeneralTreatmentsEnum.O2_MEDIUM]: '',
  [TreatmentGeneralTreatmentsEnum.O2_LOW]: '',
  [TreatmentGeneralTreatmentsEnum.SPO2_MEASUREMENT]: '',
  [TreatmentGeneralTreatmentsEnum.DRUG_ADMINISTRATION]: '',
  [TreatmentGeneralTreatmentsEnum.GLUCOSE_ADMINISTRATION]: '',
  [TreatmentGeneralTreatmentsEnum.EPIPEN_ADMINISTRATION]: '',
  [TreatmentGeneralTreatmentsEnum.GLUCOSE_MEASUREMENT]: '',
  [TreatmentGeneralTreatmentsEnum.BT_MEASUREMENT]: '',
  [TreatmentGeneralTreatmentsEnum.BP_MEASUREMENT]: '',
  [TreatmentGeneralTreatmentsEnum.BP_MAINTENANCE_WITH_SHOCK_PANTS]: '',
  [TreatmentGeneralTreatmentsEnum.AIRWAY_MANUAL]: '',
  [TreatmentGeneralTreatmentsEnum.VENTILATION_MANUAL]: '',
  [TreatmentGeneralTreatmentsEnum.VENTILATION_AUTOMATIC]: '',
  [TreatmentGeneralTreatmentsEnum.AIRWAY_LARYNGOSCOPE_FORCEPS_SUCTION]: '',
  [TreatmentGeneralTreatmentsEnum.AIRWAY_ESOPHAGEAL_OR_OROPHARYNGEAL_TUBE]: '',
  [TreatmentGeneralTreatmentsEnum.AIRWAY_INTUBATION]: '',
  [TreatmentGeneralTreatmentsEnum.CHEST_COMPRESSION_AUTO_ON_SITE]: '',
  [TreatmentGeneralTreatmentsEnum.CHEST_COMPRESSION_AUTO_IN_VEHICLE]: '',
  [TreatmentGeneralTreatmentsEnum.IV_LINE_ESTABLISHMENT_INFUSION]: '',
  [TreatmentGeneralTreatmentsEnum.IV_ACCESS_BEFORE_CPA_GLUCOSE]: '',
  [TreatmentGeneralTreatmentsEnum.IV_ACCESS_BEFORE_CPA_SHOCK]: '',
  [TreatmentGeneralTreatmentsEnum.HEMOSTASIS_PRESSURE]: '',
  [TreatmentGeneralTreatmentsEnum.HEMOSTASIS_DEVICE]: '',
  [TreatmentGeneralTreatmentsEnum.POSITION_MANAGEMENT]: '',
  [TreatmentGeneralTreatmentsEnum.IMMOBILIZATION]: '',
  [TreatmentGeneralTreatmentsEnum.HEAD_STABILIZATION]: '',
  [TreatmentGeneralTreatmentsEnum.LOCAL_STABILIZATION]: '',
  [TreatmentGeneralTreatmentsEnum.WARMING]: '',
  [TreatmentGeneralTreatmentsEnum.COOLING]: '',
  [TreatmentGeneralTreatmentsEnum.HOME_TREATMENT_INFUSION]: '',
  [TreatmentGeneralTreatmentsEnum.HOME_TREATMENT_EXTERNAL_FISTULA]: '',
  [TreatmentGeneralTreatmentsEnum.HOME_TREATMENT_OTHER]: '',
  [TreatmentGeneralTreatmentsEnum.OTHER]: '',
} as const satisfies Record<
  keyof typeof TreatmentGeneralTreatmentsEnum,
  string
>;

/**
 * 一般治療の日本語ラベルを取得する関数
 *
 * @param generalTreatments - 一般治療
 * @returns 日本語ラベル
 */
export const getTreatmentGeneralTreatmentsLabel = (
  generalTreatments:
    | keyof typeof TreatmentGeneralTreatmentsEnum
    | null
    | undefined
): string => {
  if (
    !generalTreatments ||
    !(generalTreatments in TREATMENT_GENERAL_TREATMENTS_LABELS)
  ) {
    return '';
  }
  return TREATMENT_GENERAL_TREATMENTS_LABELS[generalTreatments];
};

/**
 * ROSCの日本語ラベルマッピング
 */
const ROSC_TREATMENT_LABELS = {
  [TreatmentROSCTreatmentsEnum.DC]: 'DC',
  [TreatmentROSCTreatmentsEnum.ADRENALINE_IV]: '静脈内',
  [TreatmentROSCTreatmentsEnum.ADRENALINE_IM]: '筋注',
  [TreatmentROSCTreatmentsEnum.EPIPEN]: 'エピペン',
  [TreatmentROSCTreatmentsEnum.GLUCOSE]: 'ブドウ糖',
} as const satisfies Record<keyof typeof TreatmentROSCTreatmentsEnum, string>;

/**
 * ROSCの日本語ラベルを取得する関数
 *
 * @param treatment - ROSC
 * @returns 日本語ラベル
 */
export const getROSCTreatmentLabel = (
  treatment: keyof typeof TreatmentROSCTreatmentsEnum | null | undefined
): string => {
  if (!treatment || !(treatment in ROSC_TREATMENT_LABELS)) {
    return '';
  }
  return ROSC_TREATMENT_LABELS[treatment];
};

/**
 * ROSCの追加治療情報を取得する関数
 *
 * @param roscData - ROSC
 * @returns 追加治療情報
 */
const getROSCAdditionalTreatmentInfo = (roscData: TreatmentROSCData) => {
  const treatment = roscData.treatments?.[0] ?? '';

  if (!treatment) return '';
  if (treatment === TreatmentROSCTreatmentsEnum.DC) {
    return getTreatmentROSCECGWaveformLabel(roscData.ecgWaveForm);
  }
  if (treatment === TreatmentROSCTreatmentsEnum.ADRENALINE_IV) {
    return roscData.adrenalineIv ?? '';
  }
  if (treatment === TreatmentROSCTreatmentsEnum.ADRENALINE_IM) {
    return roscData.adrenalineIm ?? '';
  }
  if (treatment === TreatmentROSCTreatmentsEnum.EPIPEN) {
    return roscData.epipen ?? '';
  }
  if (treatment === TreatmentROSCTreatmentsEnum.GLUCOSE) {
    return roscData.glucose ?? '';
  }
};

/**
 * CPAの日本語ラベルマッピング
 */
const TREATMENT_CPA_TIMING_LABELS = {
  [TreatmentCPADataTimingEnum.PRESENT]: '有',
  [TreatmentCPADataTimingEnum.ABSENT]: '無',
  [TreatmentCPADataTimingEnum.AFTER_ARRIVAL]: '着後',
} as const satisfies Record<keyof typeof TreatmentCPADataTimingEnum, string>;

/**
 * CPAの日本語ラベルを取得する関数
 *
 * @param timing - CPA
 * @returns 日本語ラベル
 */
export const getTreatmentCPATimingLabel = (
  timing:
    | keyof typeof TreatmentCPADataTimingEnum
    | keyof typeof TreatmentBsCPRDataTimingEnum
    | null
    | undefined
): string => {
  if (!timing || !(timing in TREATMENT_CPA_TIMING_LABELS)) {
    return '';
  }
  return TREATMENT_CPA_TIMING_LABELS[timing];
};

const TREATMENT_CONFIDENCE_TYPE_LABELS = {
  [TreatmentCPADataConfidenceTypeEnum.ESTIMATED]: '推',
  [TreatmentCPADataConfidenceTypeEnum.CONFIRMED]: '確',
  [TreatmentCPADataConfidenceTypeEnum.UNKNOWN]: '不明',
} as const satisfies Record<
  keyof typeof TreatmentCPADataConfidenceTypeEnum,
  string
>;

export const getTreatmentConfidenceTypeLabel = (
  confidenceType:
    | keyof typeof TreatmentCPADataConfidenceTypeEnum
    | keyof typeof TreatmentBsCPRDataConfidenceTypeEnum
    | null
    | undefined
): string => {
  if (
    !confidenceType ||
    !(confidenceType in TREATMENT_CONFIDENCE_TYPE_LABELS)
  ) {
    return '';
  }
  return TREATMENT_CONFIDENCE_TYPE_LABELS[confidenceType];
};

const TREATMENT_AGREED_BY_LABELS = {
  [TreatmentAgreedByEnum.FAMILY]: '家族',
  [TreatmentAgreedByEnum.RELATED]: '関係者',
  [TreatmentAgreedByEnum.NONE]: '未取得',
} as const satisfies Record<keyof typeof TreatmentAgreedByEnum, string>;

export const getTreatmentAgreedByLabel = (
  agreedBy: keyof typeof TreatmentAgreedByEnum | null | undefined
): string => {
  if (!agreedBy || !(agreedBy in TREATMENT_AGREED_BY_LABELS)) {
    return '';
  }
  return TREATMENT_AGREED_BY_LABELS[agreedBy];
};

const TREATMENT_EXECUTE_TREATMENT_LABELS = {
  [TreatmentExecuteTreatmentEnum.BLOOD_GLU_MEASUREMENT]: '血糖測定',
  [TreatmentExecuteTreatmentEnum.GLUCOSE]: 'ブドウ糖',
  [TreatmentExecuteTreatmentEnum.IV_ACCESS_BEFORE_CPA]: '静脈路',
  [TreatmentExecuteTreatmentEnum.AIRWAY_ESTABLISHMENT]: '気道確保',
  [TreatmentExecuteTreatmentEnum.ADRENALINE_IV_CPA]:
    'アドレナリン(静脈内：CPA)',
  [TreatmentExecuteTreatmentEnum.ADRENALINE_IM_ANAPHYAXIS]:
    'アドレナリン(筋注：アナフィラキシー)',
  [TreatmentExecuteTreatmentEnum.EPIPEN]: 'エピペン',
  [TreatmentExecuteTreatmentEnum.ACP]: 'ACP',
} as const satisfies Record<keyof typeof TreatmentExecuteTreatmentEnum, string>;

export const getTreatmentExecuteTreatmentLabel = (
  executeTreatment:
    | keyof typeof TreatmentExecuteTreatmentEnum
    | null
    | undefined
): string => {
  if (
    !executeTreatment ||
    !(executeTreatment in TREATMENT_EXECUTE_TREATMENT_LABELS)
  ) {
    return '';
  }
  return TREATMENT_EXECUTE_TREATMENT_LABELS[executeTreatment];
};

const TREATMENT_INITIAL_ECG_LABELS = {
  [TreatmentInitialEcgEnum.NORMAL]: '正常',
  [TreatmentInitialEcgEnum.VF]: 'VF',
  [TreatmentInitialEcgEnum.VT]: 'VT',
  [TreatmentInitialEcgEnum.PEA]: 'PEA',
  [TreatmentInitialEcgEnum.ASYSTOLE]: 'Asys',
  [TreatmentInitialEcgEnum.OTHER]: '他',
} as const satisfies Record<keyof typeof TreatmentInitialEcgEnum, string>;

export const getTreatmentInitialEcgLabel = (
  initialEcg: keyof typeof TreatmentInitialEcgEnum | null | undefined
): string => {
  if (!initialEcg || !(initialEcg in TREATMENT_INITIAL_ECG_LABELS)) {
    return '';
  }
  return TREATMENT_INITIAL_ECG_LABELS[initialEcg];
};

const TREATMENT_IV_ACCESS_EXECUTE_LABELS = {
  [TreatmentIVAccessExecuteEnum.PERFORMED]: '可',
  [TreatmentIVAccessExecuteEnum.NOT_PERFORMED]: '否',
  [TreatmentIVAccessExecuteEnum.REMOVED_PRE_HOSPITAL]: '病院前抜去',
} as const satisfies Record<keyof typeof TreatmentIVAccessExecuteEnum, string>;

export const getTreatmentIVAccessExecuteLabel = (
  ivAccessExecute: keyof typeof TreatmentIVAccessExecuteEnum | null | undefined
): string => {
  if (
    !ivAccessExecute ||
    !(ivAccessExecute in TREATMENT_IV_ACCESS_EXECUTE_LABELS)
  ) {
    return '';
  }
  return TREATMENT_IV_ACCESS_EXECUTE_LABELS[ivAccessExecute];
};

const TREATMENT_IV_ACCESS_SIDE_LABELS = {
  [TreatmentIVAccessSideEnum.RIGHT]: '右',
  [TreatmentIVAccessSideEnum.LEFT]: '左',
} as const satisfies Record<keyof typeof TreatmentIVAccessSideEnum, string>;

export const getTreatmentIVAccessSideLabel = (
  ivAccessSide: keyof typeof TreatmentIVAccessSideEnum | null | undefined
): string => {
  if (!ivAccessSide || !(ivAccessSide in TREATMENT_IV_ACCESS_SIDE_LABELS)) {
    return '';
  }
  return TREATMENT_IV_ACCESS_SIDE_LABELS[ivAccessSide];
};

const TREATMENT_IV_ACCESS_PART_LABELS = {
  [TreatmentIVAccessPartEnum.BACK_OF_HAND]: '手背',
  [TreatmentIVAccessPartEnum.FOREARM]: '前腕',
  [TreatmentIVAccessPartEnum.MIDLINE]: '正中',
  [TreatmentIVAccessPartEnum.DORSUM_FOOT]: '足背',
  [TreatmentIVAccessPartEnum.OTHER]: '他',
} as const satisfies Record<keyof typeof TreatmentIVAccessPartEnum, string>;

export const getTreatmentIVAccessPartLabel = (
  ivAccessPart: keyof typeof TreatmentIVAccessPartEnum | null | undefined
): string => {
  if (!ivAccessPart || !(ivAccessPart in TREATMENT_IV_ACCESS_PART_LABELS)) {
    return '';
  }
  return TREATMENT_IV_ACCESS_PART_LABELS[ivAccessPart];
};

const TREATMENT_IV_ACCESS_INFUSION_RATE_LABELS = {
  [TreatmentInfusionRateEnum.RAPID]: '急速',
  [TreatmentInfusionRateEnum.MAINTENANCE]: '維持',
} as const satisfies Record<keyof typeof TreatmentInfusionRateEnum, string>;

export const getTreatmentIVAccessInfusionRateLabel = (
  infusionRate: keyof typeof TreatmentInfusionRateEnum | null | undefined
): string => {
  if (
    !infusionRate ||
    !(infusionRate in TREATMENT_IV_ACCESS_INFUSION_RATE_LABELS)
  ) {
    return '';
  }
  return TREATMENT_IV_ACCESS_INFUSION_RATE_LABELS[infusionRate];
};

const TREATMENT_AIRWAY_MANAGEMENT_DEVICE_TYPE_LABELS = {
  [TreatmentAirwayManagementDeviceTypeEnum.AIRWAY_MANAGEMENT]: '気管挿管',
  [TreatmentAirwayManagementDeviceTypeEnum.LT]: 'LT',
  [TreatmentAirwayManagementDeviceTypeEnum.LM]: 'LM',
  [TreatmentAirwayManagementDeviceTypeEnum.CB]: 'CB',
  [TreatmentAirwayManagementDeviceTypeEnum.OTHER]: '他',
} as const satisfies Record<
  keyof typeof TreatmentAirwayManagementDeviceTypeEnum,
  string
>;

export const getTreatmentAirwayManagementDeviceTypeLabel = (
  deviceType:
    | keyof typeof TreatmentAirwayManagementDeviceTypeEnum
    | null
    | undefined
): string => {
  if (
    !deviceType ||
    !(deviceType in TREATMENT_AIRWAY_MANAGEMENT_DEVICE_TYPE_LABELS)
  ) {
    return '';
  }
  return TREATMENT_AIRWAY_MANAGEMENT_DEVICE_TYPE_LABELS[deviceType];
};

const TREATMENT_AIRWAY_MANAGEMENT_LARYNGOSCOPY_LABELS = {
  [TreatmentAirwayManagementLaryngoscopyEnum.DIRECT]: '直視',
  [TreatmentAirwayManagementLaryngoscopyEnum.VIDEO]: 'ビデオ',
} as const satisfies Record<
  keyof typeof TreatmentAirwayManagementLaryngoscopyEnum,
  string
>;

export const getTreatmentAirwayManagementLaryngoscopyLabel = (
  laryngoscopy:
    | keyof typeof TreatmentAirwayManagementLaryngoscopyEnum
    | null
    | undefined
): string => {
  if (
    !laryngoscopy ||
    !(laryngoscopy in TREATMENT_AIRWAY_MANAGEMENT_LARYNGOSCOPY_LABELS)
  ) {
    return '';
  }
  return TREATMENT_AIRWAY_MANAGEMENT_LARYNGOSCOPY_LABELS[laryngoscopy];
};

const TREATMENT_AIRWAY_MANAGEMENT_CUFF_VALUE_TYPE_LABELS = {
  [TreatmentAirwayManagementCuffValueTypeEnum.NUMBER_10]: '10',
  [TreatmentAirwayManagementCuffValueTypeEnum.OTHER]: 'その他',
} as const;

export const getTreatmentAirwayManagementCuffValueTypeLabel = (
  cuffValueType: TreatmentAirwayManagementCuffValueTypeEnum | null | undefined
): string => {
  if (
    !cuffValueType ||
    !(cuffValueType in TREATMENT_AIRWAY_MANAGEMENT_CUFF_VALUE_TYPE_LABELS)
  ) {
    return '';
  }
  return TREATMENT_AIRWAY_MANAGEMENT_CUFF_VALUE_TYPE_LABELS[cuffValueType];
};

const TREATMENT_AIRWAY_MANAGEMENT_VENTILATION_LABELS = {
  [TreatmentAirwayManagementVentilationEnum.MANUAL]: '用手',
  [TreatmentAirwayManagementVentilationEnum.EQUIPMENT]: '機械',
  [TreatmentAirwayManagementVentilationEnum.OTHER]: '他',
} as const satisfies Record<
  keyof typeof TreatmentAirwayManagementVentilationEnum,
  string
>;

export const getTreatmentAirwayManagementVentilationLabel = (
  ventilation: TreatmentAirwayManagementVentilationEnum | null | undefined
): string => {
  if (
    !ventilation ||
    !(ventilation in TREATMENT_AIRWAY_MANAGEMENT_VENTILATION_LABELS)
  ) {
    return '';
  }
  return TREATMENT_AIRWAY_MANAGEMENT_VENTILATION_LABELS[ventilation];
};

const TREATMENT_AIRWAY_MANAGEMENT_EXECUTE_LABELS = {
  [TreatmentAirwayManagementExecuteEnum.SUCCESS]: '成功',
  [TreatmentAirwayManagementExecuteEnum.REMOVED]: '抜去',
  [TreatmentAirwayManagementExecuteEnum.FAILED]: 'できず',
  [TreatmentAirwayManagementExecuteEnum.NOT_PERFORMED]: '未実施',
} as const satisfies Record<
  keyof typeof TreatmentAirwayManagementExecuteEnum,
  string
>;

export const getTreatmentAirwayManagementExecuteLabel = (
  execute: TreatmentAirwayManagementExecuteEnum | null | undefined
): string => {
  if (!execute || !(execute in TREATMENT_AIRWAY_MANAGEMENT_EXECUTE_LABELS)) {
    return '';
  }
  return TREATMENT_AIRWAY_MANAGEMENT_EXECUTE_LABELS[execute];
};

const TREATMENT_ROSC_ECG_WAVEFORM_LABELS = {
  [TreatmentROSCECGWaveFormEnum.VF]: 'VF',
  [TreatmentROSCECGWaveFormEnum.VT]: 'VT',
  [TreatmentROSCECGWaveFormEnum.PEA]: 'PEA',
  [TreatmentROSCECGWaveFormEnum.ASYSTOLE]: 'Asys',
  [TreatmentROSCECGWaveFormEnum.OTHER]: '他',
  [TreatmentROSCECGWaveFormEnum.SINUS]: 'sinus',
  [TreatmentROSCECGWaveFormEnum.ARRHYTHMIA]: '不整脈',
} as const satisfies Record<keyof typeof TreatmentROSCECGWaveFormEnum, string>;

export const getTreatmentROSCECGWaveformLabel = (
  ecgWaveform: keyof typeof TreatmentROSCECGWaveFormEnum | null | undefined
): string => {
  if (!ecgWaveform || !(ecgWaveform in TREATMENT_ROSC_ECG_WAVEFORM_LABELS)) {
    return '';
  }
  return TREATMENT_ROSC_ECG_WAVEFORM_LABELS[ecgWaveform];
};

export const enumTranslator = (
  data: ReturnType<typeof useIncidentDetail>['incidentDetailData']
) => {
  const { incidentInfo, emsTeamInfo, patientInfo } = data;
  return {
    incidentInfo: {
      ...incidentInfo,
      incidentType: getIncidentTypeLabel(incidentInfo.incidentType),
    },
    patientInfo: patientInfo.map(patient => {
      return {
        ...patient,
        baseInfo: {
          ...patient.baseInfo,
          sex: getPatientSexLabel(patient?.baseInfo?.sex),
          occupation: getOccupationLabel(patient?.baseInfo?.occupation),
          relatedPersons:
            patient?.baseInfo?.relatedPersons?.map(relatedPerson => ({
              ...relatedPerson,
              relationship: getBaseInfoRelatedPersonRelationshipLabel(
                relatedPerson.relationship
              ),
              attendantArrivalType:
                getBaseInfoRelatedPersonAttendantArrivalTypeLabel(
                  relatedPerson.attendantArrivalType
                ),
            })) ?? [],
          collaboration:
            patient?.baseInfo?.collaboration?.map(collaboration =>
              getBaseInfoCollaborationLabel(collaboration)
            ) ?? [],
          collaborationTiming: getBaseInfoCollaborationTimingLabel(
            patient?.baseInfo?.collaborationTiming
          ),
          custodyItems:
            patient?.baseInfo?.custodyItems?.map(custodyItem =>
              getBaseInfoCustodyItemLabel(custodyItem)
            ) ?? [],
        },
        requestSummary: {
          ...patient.requestSummary,
          medicalHistory:
            patient?.requestSummary?.medicalHistory?.map(history =>
              getRequestSummaryMedicalHistoryLabel(history)
            ) ?? [],
          adl: getRequestSummaryADLLabel(patient?.requestSummary?.adl),
          adlLevel: getRequestSummaryADLLevelLabel(
            patient?.requestSummary?.adlLevel
          ),
          onsetType: getRequestSummaryOnsetTypeLabel(
            patient?.requestSummary?.onsetType
          ),
          isLastMeal: getRequestSummaryIsLastMealLabel(
            patient?.requestSummary?.isLastMeal
          ),
        },
        operationCriteria: {
          ...patient.operationCriteria,
          operationCriteriaDetail: getOperationCriteriaDetailLabel(
            patient?.operationCriteria?.operationCriteriaDetail
          ),
        },
        vitals: patient.vitals?.map(vital => {
          return {
            ...vital,
            data: {
              ...vital.data,
              o2Means: getVitalO2MeanLabel(vital.data?.o2Means),
              o2FlowCondition: getVitalO2FlowConditionLabel(
                vital.data?.o2FlowCondition
              ),
              btPart: getVitalBtPartLabel(vital.data?.btPart),
              bpPart: getVitalBpPartLabel(
                vital.data?.bpPart,
                vital.data?.isUnpalpable
              ),
              ecgWaveform: getECGWaveformLabel(vital.data?.ecgWaveform),
              ecgRemarks: getECGWaveRemarksLabel(vital.data?.ecgRemarks),
              pupilReactionLeft: getPupilReactionLabel(
                vital.data?.pupilReactionLeft
              ),
              pupilReactionRight: getPupilReactionLabel(
                vital.data?.pupilReactionRight
              ),
              measureTiming: getMeasureTimingLabel(vital.data?.measureTiming),
            },
          };
        }),
        treatments: patient.treatments?.map(treatment => {
          return {
            ...treatment,
            data: {
              ...treatment.data,
              agreedBy: getTreatmentAgreedByLabel(treatment.data?.agreedBy),
              generalTreatments: treatment.data?.generalTreatments.map(
                generalTreatments =>
                  getTreatmentGeneralTreatmentsLabel(generalTreatments)
              ),
              executeTreatment: treatment.data?.executeTreatment.map(
                executeTreatment =>
                  getTreatmentExecuteTreatmentLabel(executeTreatment)
              ),
              initialEcg: getTreatmentInitialEcgLabel(
                treatment.data?.initialEcg
              ),
              ivAccessExecute: getTreatmentIVAccessExecuteLabel(
                treatment.data?.ivAccessExecute
              ),
              ivAccessTiming: getTreatmentIvAccessTimingLabel(
                treatment.data?.ivAccessTiming
              ),
              ivAccessData: treatment.data?.ivAccessData?.map(ivAccessData => ({
                ...ivAccessData,
                ivAccessSide: getTreatmentIVAccessSideLabel(
                  ivAccessData.ivAccessSide
                ),
                ivAccessPart: getTreatmentIVAccessPartLabel(
                  ivAccessData.ivAccessPart
                ),
              })),
              infusionRate: getTreatmentIVAccessInfusionRateLabel(
                treatment.data?.infusionRate
              ),
              intubationDevice: getIntubationDeviceLabel(
                treatment.data?.intubationDevice
              ),
              administeredMedication: getAdministeredMedicationLabel(
                treatment.data?.administeredMedication
              ),
              roscData: treatment.data?.roscData?.map(rosc => ({
                ...rosc,
                treatments: rosc.treatments?.map(treatment =>
                  getROSCTreatmentLabel(treatment)
                ),
                additionalTreatmentInfo: getROSCAdditionalTreatmentInfo(rosc),
              })),
              cpaData: {
                ...treatment.data?.cpaData,
                cpaTiming: getTreatmentCPATimingLabel(
                  treatment.data?.cpaData?.cpaTiming
                ),
                confidenceType: getTreatmentConfidenceTypeLabel(
                  treatment.data?.cpaData?.confidenceType
                ),
              },
              bscprData: {
                ...treatment.data?.bscprData,
                bscprTiming: getTreatmentCPATimingLabel(
                  treatment.data?.bscprData?.bscprTiming
                ),
                confidenceType: getTreatmentConfidenceTypeLabel(
                  treatment.data?.bscprData?.confidenceType
                ),
              },
              dcData: {
                ...treatment.data?.dcData,
                dcTiming: getTreatmentCPATimingLabel(
                  treatment.data?.dcData?.dcTiming
                ),
              },
              airwayManagementData: {
                ...treatment.data?.airwayManagementData,
                deviceType: getTreatmentAirwayManagementDeviceTypeLabel(
                  treatment.data?.airwayManagementData?.deviceType
                ),
                laryngoscopy: getTreatmentAirwayManagementLaryngoscopyLabel(
                  treatment.data?.airwayManagementData?.laryngoscopy
                ),
                cuffValueType: getTreatmentAirwayManagementCuffValueTypeLabel(
                  treatment.data?.airwayManagementData?.cuffValueType
                ),
                ventilation: getTreatmentAirwayManagementVentilationLabel(
                  treatment.data?.airwayManagementData?.ventilation
                ),
              },
              airwayManagementExecute: getTreatmentAirwayManagementExecuteLabel(
                treatment.data?.airwayManagementExecute
              ),
            },
          };
        }),
        trafficAccident: {
          ...patient.trafficAccident,
          accidentType: patient.trafficAccident?.accidentType.map(type =>
            getTrafficAccidentAccidentTypeLabel(type)
          ),
          anotherSide: {
            ...patient.trafficAccident?.anotherSide,
            accidentSituation: getTrafficAccidentSituationLabel(
              patient.trafficAccident?.anotherSide?.accidentSituation
            ),
          },
        },
        drCarDrHeli: {
          ...patient.drCarDrHeli,
          drCarDispatch: getDrCarDrHeliDispatchLabel(
            patient.drCarDrHeli?.drCarDispatch
          ),
          drHeliDispatch: getDrCarDrHeliDispatchLabel(
            patient.drCarDrHeli?.drHeliDispatch
          ),
        },
        findingsEmsContact: {
          ...patient.findingsEmsContact,
          conditions: getFindingsEmsContactConditionsLabel(
            patient.findingsEmsContact?.reveals?.safetyEquipments,
            patient.findingsEmsContact?.reveals?.isHomeOxygen,
            patient.findingsEmsContact?.reveals?.isOnDialysis,
            patient.findingsEmsContact?.reveals?.isPregnant
          ),
          just7: patient.findingsEmsContact?.just7.map(just7 =>
            getJust7Label(just7)
          ),
          reveals: {
            ...patient.findingsEmsContact?.reveals,
            paralysisParts:
              patient.findingsEmsContact?.reveals?.paralysisParts.map(
                paralysisParts =>
                  getFindEmsContactParalysisPartsLabel(paralysisParts)
              ),
            paralysisSymptoms:
              patient.findingsEmsContact?.reveals?.paralysisSymptoms.map(
                paralysisSymptoms =>
                  getFindEmsContactParalysisSymptomsLabel(paralysisSymptoms)
              ),
            spasmParts: patient.findingsEmsContact?.reveals?.spasmParts.map(
              spasmParts => getFindEmsContactSpasmPartsLabel(spasmParts)
            ),
            bleedingType: patient.findingsEmsContact?.reveals?.bleedingType.map(
              bleedingType => getFindEmsContactBleedingTypeLabel(bleedingType)
            ),
          },
        },
      };
    }),
    emsTeamInfo: {
      ...emsTeamInfo,
      emsTeamMembers: emsTeamInfo.emsTeamMembers.map(member => ({
        ...member,
        position: getEmsTeamMemberPositionLabel(member.position),
      })),
    },
  };
};
